import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import type {
  BreakRequestDecisionSummary,
  BreakRequestProposal,
} from '@runhuman/shared';
import { buildRequestHistoryEntries, type HistoryTone } from './request-history';
import type { BreakRequestsTheme } from './types';

/** The shape the proposals endpoint returns (#4861): rounds + terminal decision. */
export interface RequestHistoryData {
  readonly proposals: readonly BreakRequestProposal[];
  readonly decision?: BreakRequestDecisionSummary | null;
}

export interface BreakProposalChainProps {
  /**
   * Surface-supplied loader. Each call site passes its own auth-aware fetcher
   * (web admin → Clerk-token API client; iframe admin → Discord OAuth bearer
   * client; tester surface → tester-token API client) so this widget never
   * needs to know which API surface the chain lives behind. Returns the full
   * request-history payload — rounds plus the terminal decision (#4861).
   */
  fetchProposals: () => Promise<RequestHistoryData>;
  /**
   * Whether to expose the toggle at all. A `false` value hides the widget
   * outright — used by the dense schedule-row surface, which only wants the
   * history affordance once a negotiation has actually happened. The inbox
   * dialog passes `true` so the full timeline (original request → decision)
   * is always reachable.
   */
  hasCounterRounds: boolean;
  /**
   * The viewer's Runhuman user id. Frames each entry's actor as "you" when the
   * acting user id matches, the actor's resolved name when it doesn't, and an
   * identity-less "<Verb> on <when>" when the actor can't be resolved (#4999).
   * `null` when the surface can't resolve the viewer's id — every entry then
   * falls back to the name-or-identity-less framing (never "you").
   */
  viewerUserId: string | null;
  timezone: string;
  theme: BreakRequestsTheme;
}

/**
 * Per-request "Request history" (#3772, redesigned #4861). Renders the
 * negotiation as a plain-language timeline — "Requested by you on …",
 * "Countered by <Floor Manager> on …", "Denied by … on …" — one entry per
 * round in `roundIndex ASC` order, closed by the terminal decision when the
 * request is settled.
 *
 * Lazy-fetched on first open: the history endpoint hits a separate table from
 * the panel's main list, so eagerly loading for every collapsed row would
 * multiply the panel's query count for no UI gain.
 */
export function BreakProposalChain({
  fetchProposals,
  hasCounterRounds,
  viewerUserId,
  timezone,
  theme,
}: BreakProposalChainProps) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [data, setData] = useState<RequestHistoryData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const styles = useMemo(() => buildStyles(theme), [theme]);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setData(await fetchProposals());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load history');
    } finally {
      setBusy(false);
    }
  }, [fetchProposals]);

  useEffect(() => {
    if (open && data === null && !busy) void load();
  }, [open, data, busy, load]);

  const entries = useMemo(
    () =>
      data === null
        ? []
        : buildRequestHistoryEntries(data.proposals, data.decision, viewerUserId, timezone),
    [data, viewerUserId, timezone],
  );

  if (!hasCounterRounds) return null;

  return (
    <div style={styles.container}>
      <button
        type="button"
        style={hovered ? { ...styles.toggle, color: theme.colors.textPrimary } : styles.toggle}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-expanded={open}
      >
        {open ? '▾' : '▸'} Request history
      </button>
      {open && (
        <div style={styles.list}>
          {busy && <div style={styles.muted}>Loading…</div>}
          {error && <div style={styles.error}>{error}</div>}
          {entries.map((entry, idx) => (
            <div key={entry.key} style={idx === 0 ? styles.item : styles.itemDivided}>
              <span style={{ ...styles.icon, color: toneColorOf(entry.tone, theme) }}>
                {TONE_GLYPH[entry.tone]}
              </span>
              <div style={styles.row}>
                <span style={headlineStyles(entry.tone, theme)}>{entry.headline}</span>
                {entry.window && <span style={styles.window}>{entry.window}</span>}
                {entry.detail && <span style={styles.rationale}>“{entry.detail}”</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface ChainStyles {
  readonly container: CSSProperties;
  readonly toggle: CSSProperties;
  readonly list: CSSProperties;
  readonly muted: CSSProperties;
  readonly error: CSSProperties;
  readonly item: CSSProperties;
  readonly itemDivided: CSSProperties;
  readonly icon: CSSProperties;
  readonly row: CSSProperties;
  readonly window: CSSProperties;
  readonly rationale: CSSProperties;
}

function buildStyles(theme: BreakRequestsTheme): ChainStyles {
  // Each entry is a [tone-icon] [content] row; items after the first carry a
  // top divider so each one's start/end is unmistakable (#5143).
  const item: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
  };
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    toggle: {
      alignSelf: 'flex-start',
      background: 'transparent',
      border: 'none',
      color: theme.colors.textSecondary,
      fontSize: theme.fontSize.xs,
      padding: 0,
      cursor: 'pointer',
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
    },
    muted: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.textMuted,
      fontStyle: 'italic',
    },
    error: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.error,
    },
    item,
    itemDivided: { ...item, borderTop: `1px solid ${theme.colors.border}` },
    icon: {
      flexShrink: 0,
      width: 14,
      textAlign: 'center',
      fontSize: theme.fontSize.xs,
      fontWeight: 700,
      lineHeight: 1.5,
    },
    row: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      fontSize: theme.fontSize.xs,
      color: theme.colors.textSecondary,
    },
    window: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.textPrimary,
    },
    rationale: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.textMuted,
      fontStyle: 'italic',
    },
  };
}

// Tones mirror the dialog's status-pill palette so the history's closing line
// reads the same color as the title badge (#4861): green confirm, red deny.
const TONE_COLOR: Readonly<Record<HistoryTone, string | null>> = {
  neutral: null,
  confirmed: '#10b981',
  denied: '#ef4444',
  cancelled: '#6b7280',
};

// Leading glyph per entry (#5143) — marks where each item begins and reinforces
// the outcome tone (request • / confirmed ✓ / denied ✕ / cancelled ⊘).
const TONE_GLYPH: Readonly<Record<HistoryTone, string>> = {
  neutral: '•',
  confirmed: '✓',
  denied: '✕',
  cancelled: '⊘',
};

// Resolve a tone to a concrete color — `neutral` (no own color) reads as the
// secondary text color. Shared by the entry icon and headline so they match.
function toneColorOf(tone: HistoryTone, theme: BreakRequestsTheme): string {
  const toneColor = TONE_COLOR[tone];
  return toneColor === null ? theme.colors.textSecondary : toneColor;
}

function headlineStyles(tone: HistoryTone, theme: BreakRequestsTheme): CSSProperties {
  return {
    fontSize: theme.fontSize.xs,
    fontWeight: 600,
    color: toneColorOf(tone, theme),
  };
}
